import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/app/hooks";
import { visibility } from "../../redux/slices/rightSideAd";
import images from "../../assets/images";
import "./AdminSystemMessages.scss";
import Loader from "../../components/Loader/Loader";
import { useLazyQuery, useMutation } from "@apollo/client";
import { READ_SYSTEM_MESSAGES, SYSTEM_MESSAGES, SYSTEM_MESSAGES_TOTAL_COUNT } from "./api/AdminSystemMessages";
import ToasterWithMessage from "../../components/toaster";
import FileViewer from "../../components/FileViewer/FileViewer";
import { setSystemMessagesCount } from "../../redux/slices/authSlice";
import FileViewerSystemMessages from "../../components/FileViewerForSystemMessages/FileViewerForSystemMessages";
import { getScreenDimensions } from "../../utils/helper";
import MobileScreenHeader from "../../components/MobileScreenHeader/MobileScreenHeader";
import { FETCH_POLICY } from "../../utils/constants";

enum UpdateType {
  SystemInfo = "systeminfo",
  SystemUpdate = "systemupdate",
  Tips = "tips",
}

interface SystemMessageFeed {
  fId: string;
}

interface SystemMessage {
  categories: string[];
  createdAt: string;
  deletedAt: string | null;
  description: string;
  documentLink: string | null;
  documentType: string | null;
  heading: string;
  id: string;
  messageId: number;
  priority: "low" | "medium" | "high"; // You can also use an enum for this
  title: string;
  type: "tips" | "systeminfo" | "systemupdate"; // Similar to priority, you could use an enum
  updatedAt: string;
  feedsBySmId: {
    nodes: SystemMessageFeed[];
  };
}

interface AllSystemMessages {
  nodes: SystemMessage[];
  totalCount: number;
}

interface SystemMessagesResponse {
  allSystemMessages: AllSystemMessages;
}

const getDocumentIcon = (documentType: string): string => {
  const { documentsystemMessages, pdfImg, powerpoint, excelPreview } = images;
  switch (documentType) {
    case "docx":
      return documentsystemMessages; // Update this path to your actual document system messages icon
    case "pdf":
      return pdfImg; // Path for PDF icon
    case "excel":
      return excelPreview;
    case "ppt":
      return powerpoint;
    default:
      return documentsystemMessages; // Return an empty string or a default image path for no document
  }
};

const AdminSystemMessages = () => {
  const dispatch = useAppDispatch();
  dispatch(visibility({ isVisible: true }));
  const { acknowledgement, system_update } = images;
  const [loading, setLoading] = useState<boolean>(false);
  const [systemMessages, setSystemMessages] = useState<SystemMessage[]>();
  const { error } = ToasterWithMessage();
  const [openPdf, setOpenPdf] = useState(false);
  const [pdfFileData, setPdfFileData] = useState<SystemMessage>();
  const { backArrow } = images;
  const profile = useAppSelector((state) => state.auth.adminProfile);
  const role = useAppSelector((state) => state.auth.role);
  const profileImage = useAppSelector((state) => state?.auth?.adminProfileImage);
  const StudentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;


  const [systemMessagesQuery] = useLazyQuery(SYSTEM_MESSAGES, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (data: SystemMessagesResponse) => {
      setLoading(false);
      setSystemMessages(data.allSystemMessages.nodes);
    },
    onError: (err) => {
      error(err.message);
    },
  });

  useEffect(() => {
    setLoading(true);
    systemMessagesQuery({
      variables: {
        first: null,
        offset: null,
      },
    });
  }, []);

  const handleClickInfo = (data: SystemMessage) => {
    setOpenPdf(true);
    setPdfFileData(data);
  };

  const handleClosePdf = () => {
    setOpenPdf(false);
    setPdfFileData(undefined);
    setLoading(true)
    systemMessagesQuery({
      variables: {
        first: null,
        offset: null,
      },
    });
  };

  const baseUrl = process.env.REACT_APP_KATON_FRONT_END_URL;

  const [readSystemMessages] = useMutation(READ_SYSTEM_MESSAGES, {
    fetchPolicy: FETCH_POLICY,
    onCompleted : (data) =>{
      systemMessagesTotalCount()
    },
    onError: (err) =>{
      console.log(err.message)
    }
  })

  const [systemMessagesTotalCount] = useLazyQuery(SYSTEM_MESSAGES_TOTAL_COUNT, {
    fetchPolicy : FETCH_POLICY,
    onCompleted : (data) => {
      dispatch(setSystemMessagesCount(data.allSystemMessages.totalCount))
    }
  })

  useEffect(() => {
    if (systemMessages && systemMessages?.length > 0 ) {
      systemMessages?.forEach((data) => {
        if (data?.feedsBySmId?.nodes[0]?.fId) {
          readSystemMessages({
            variables: {
              fId: data.feedsBySmId.nodes[0].fId,
              uId: profile?.getCurrentUser?.nodes[0]?.uId
            }
          })
        }
      });
    }
  },[profile?.getCurrentUser?.nodes, readSystemMessages, systemMessages, systemMessagesTotalCount])

  return (
    <div
      className="SystemMessages"
      style={{
        alignSelf: (loading  && !getScreenDimensions()?.isMobileScreen ) ? "center" : "flex-start",
      }}
    >
      { getScreenDimensions()?.isMobileScreen && 
      <MobileScreenHeader 
       profileImage={profileImage}
       role={role}
       studentName={StudentName}
       profile={profile}
       /> }
       <div className="systemMessagesMobileHeaderContainer">
          <div className="mobileSystemMessagesTitleTextStyle">System Messages</div>
      </div>
      {loading ? (
        <Loader /> // Show loader when loading
      ) : systemMessages && systemMessages?.length <= 0 ? (
        <div className="noData">
          <span className="noDataFoundStyle"> No data found </span>
        </div>
      ) : openPdf ? (
        <div className="documentcontainer">
           <img
          src={backArrow}
          alt="backButton"
          className="backArrow"
          onClick={() =>handleClosePdf() }
        />
          {pdfFileData?.documentType === "pdf" ? (
            <div className="excelOuterContainer">
              <FileViewerSystemMessages
                fileUrl={
                  pdfFileData?.documentLink ? baseUrl + pdfFileData?.documentLink : ""
                }
                fileData={undefined}
                onClose={handleClosePdf}
                type={"system_messages"}
                fileType={pdfFileData.documentType}
              />
            </div>
          ) : pdfFileData?.documentType === "excel" ? (
            <div className="excelOuterContainer">
              <FileViewerSystemMessages
                fileUrl={
                  pdfFileData?.documentLink
                    ?  baseUrl + pdfFileData?.documentLink
                    : ""
                }
                fileData={undefined}
                onClose={handleClosePdf}
                type="system_messages"
                fileType= {pdfFileData.documentType}
              />
            </div>
          ) : pdfFileData?.documentType === "docx" ||
            pdfFileData?.documentType === "ppt" ? (
            <div className="excelOuterContainer">
              <FileViewer
                fileUrl={
                  pdfFileData?.documentLink ? baseUrl + pdfFileData.documentLink : ""
                }
                fileData={undefined}
                onClose={handleClosePdf}
                fileType={pdfFileData?.documentType === "docx" ? "doc" : "ppt"}
                type="system_messages"
              />
            </div>
          ) : (
            <></>
          )}
        </div>
      ) : (
        <div className="SystemMessagesContainer">
          {
            <div className="EventInnerContainer">
              <div className="Events">
                {systemMessages?.map((update, index) => (
                  <div
                    className="EventCard"
                    style={{
                      backgroundColor:
                        update.type === UpdateType.SystemUpdate
                          ? "#EDFAFF"
                          : update.type === UpdateType.Tips
                          ? "#FFF0DC"
                          : update.type === UpdateType.SystemInfo
                          ? "#F3F7FA"
                          : "",
                      borderLeft:
                        update.type === UpdateType.SystemUpdate
                          ? "10px solid #858EF3"
                          : update.type === UpdateType.Tips
                          ? "10px solid #FFBE81"
                          : update.type === UpdateType.SystemInfo
                          ? "10px solid #27A2DB"
                          : "",
                      cursor:
                        update.type === UpdateType.SystemInfo
                          ? "pointer"
                          : undefined,
                    }}
                    key={index}
                    onClick={() =>
                      update.type === UpdateType.SystemInfo
                        ? handleClickInfo(update)
                        : undefined
                    }
                  >
                    {update.type === UpdateType.SystemUpdate && (
                      <>
                        <div className="EventCardTitle">
                          <div className="title-div">
                            <img
                              src={system_update} // Adjust the image path as needed
                              alt="system_update"
                              style={{
                                position: "relative",
                                bottom: "2px",
                              }}
                            />
                            <div className="title">
                              {update.title.toUpperCase()}
                            </div>
                          </div>
                          {/* <div className="view">View</div> */}
                        </div>
                        <div className="systemUpdatedescription">
                          {update.description}
                        </div>
                      </>
                    )}

                    {update.type === UpdateType.SystemInfo && (
                      <>
                        <div className="EventCardTitle">
                          <div className="title-div">
                            <div className="title">
                              {update.title.toUpperCase()}
                            </div>
                          </div>
                          <div className="view">View</div>
                        </div>
                        <div className="systemInfo">
                          <div className="documentImage">
                            <img
                              src={getDocumentIcon(update.documentType ?? "")}
                              alt="document"
                              className="imageIcon"
                            />{" "}
                          </div>
                          <div className="systemInfoDescription">
                            {" "}
                            {update.description}
                          </div>
                        </div>
                      </>
                    )}

                    {update.type === UpdateType.Tips && (
                      <div className={getScreenDimensions()?.isMobileScreen ? "tipsContainer" : ""}>
                        <div>
                        <div className="tips">
                          {!getScreenDimensions()?.isMobileScreen &&
                          <div className="tips-image">
                            <img src={acknowledgement} alt="tips" />
                          </div>}
                          <div className="tips-title">{update.title}</div>
                        </div>
                        <div className="tipscardDescription">
                          {update.description}
                        </div>
                        </div>
                        {getScreenDimensions()?.isMobileScreen &&
                          <div className="tips-image">
                            <img src={acknowledgement} alt="tips" />
                          </div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          }
        </div>
      )}
    </div>
  );
};

export default AdminSystemMessages;
